import React, {useCallback, useMemo, useRef, useState} from 'react';
import {AgGridReact} from "ag-grid-react";
import {toast} from "react-toastify";
import AttachButtonGroupCellRenderer from "./cell-renderer/attach-button-group-cell-renderer";
import {AlertMessage} from "../utils/alert-message";
import {LocalizationAgGrid} from "../utils/localization-ag-grid";
import Button from "./button";
import {FaPlus} from "react-icons/fa";
import attachService from "../services/attach-service";
import AttachesButton from "./attaches-button";
import InputForm from "./input-form";
import Modal from "./modal";

const AttachesTable = ({attaches, gridRef, onlySeeAttendances, fileId = "file-input", wrapperClass = "mt-5", header = true, canAdd = true, isVersion = false, submit, onAttachesChange, readOnly = false}) => {
    const [open, setOpen] = useState(false);
    const [imgUrl, setImgUrl] = useState("")
    const [modalApi, setModalApi] = useState(null)
    const [modalNode, setModalNode] = useState(null)
    const [modalBody, setModalBody] = useState("")
    const [modalData, setModalData] = useState([])
    const [editingRowIndex, setEditingRowIndex] = useState(null);

    const fileRef = useRef();

    const openFile = async (params, api, node) => {
        const url = await getAttachUrl(node);
        setModalData(params?.node.data)
        setEditingRowIndex(params?.node.id)
        setModalApi(api)
        setModalNode(node)
        setImgUrl(url);
        setOpen(true);
    }
    const [columnDefs] = useState([
        {field: "description", headerName: "Descrição", flex: 1, minWidth: 250, editable: !readOnly},
        {field: "name", headerName: "Anexo", flex: 1, minWidth: 250, editable: false},
        !onlySeeAttendances && {
            field: "",
            headerName: "Ações",
            sortable: false,
            editable: false,
            cellRenderer: AttachesButton,
            cellRendererParams: {
                openFile: openFile,
                readOnly,
            }
        }].filter(Boolean)
    );

    const defaultColDef = useMemo(() => ({resizable: false}), []);

    const addAttach = useCallback((newAttaches) => {
        if (!newAttaches?.length) {
            toast.warn("Escolha um arquivo!");
            return
        }

        gridRef.current?.api.applyTransaction({add: newAttaches});

        fileRef.current.value = null;
    }, [gridRef]);

    const handleFile = (event) => {
        event.preventDefault();
        let droppedFiles = "";

        if(event._reactName === "onDrop"){
             droppedFiles = event.dataTransfer.files;

        }else{
             droppedFiles = event.target.files;
        }

        const attaches = [];
        for (let i = 0; i < droppedFiles.length; i++) {
            let message = "";
            const file = droppedFiles[i];

            if (file.size > 10240000) {
                message = AlertMessage.imageInvalid;
            }

            if (file.type !== "image/png" &&
                file.type !== "image/jpeg" &&
                file.type !== "application/pdf" &&
                file.type !== "text/plain" &&
                file.type !== "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" &&
                file.type !== "application/vnd.ms-excel"
            ) {
                message = AlertMessage.archiveInvalid;
            }

            const name = file.name;
            const size = file.size;
            const extension = file.type;

            gridRef.current?.api.forEachNode((node) => {
                if (name === node.data.name) {
                    message = `Arquivo ${node.data.name} já adicionado.`
                }
            });

            if (attaches.some(attach => attach.name === name)) {
                message = `Arquivo ${name} já adicionado.`
            }

            if (message) {
                toast.error(message);
                continue;
            }

            attaches.push({name, size, extension, file});
        }

        const dataTransfer = new DataTransfer();
        attaches.forEach(attach => dataTransfer.items.add(attach.file));
        fileRef.current.files = dataTransfer.files;

        addAttach(attaches);
    };
    const addContent =  () => document.getElementById(fileId).click()

    const notifyAttachesChange = useCallback(({api}) => {
        if (!onAttachesChange) return;
        const rows = [];
        api.forEachNode(node => rows.push(node.data));
        onAttachesChange(rows);
    }, [onAttachesChange]);

    const getAttachUrl = async (node) => {
        const {attachId} = node.data;
        if (node.data.extension === "application/pdf" ||
            node.data.extension === "text/plain") {
            setModalBody("pdf/txt")
        }else if(node.data.extension === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
            node.data.extension === "application/vnd.ms-excel"){
            setModalBody("exel")
        }else {
            setModalBody("img");
        }

        const attach = attachId ? await attachService.getAttach(attachId) : node.data.file;

        let blob
        if (node.data.extension === "application/pdf") blob = new Blob([attach], { type: "application/pdf" });
        if (node.data.extension === "text/plain") blob = new Blob([attach], { type: "text/plain" })
        if (node.data.extension !== "application/pdf" && node.data.extension !== "text/plain") blob = new Blob([attach]);

        return window.URL.createObjectURL(blob);
    }

    const handleInput = (field, value) => {
        setModalData(value)

        gridRef.current.api.forEachNode((node) => {
            if (node.id === editingRowIndex) {
                node.setDataValue(field, value);
            }
        });
    }

    return (
        <div className={"ag-theme-quartz grid " + wrapperClass}>
            <input
                type="file"
                id={fileId}
                accept="image/png, image/jpeg, application/pdf, text/plain, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                multiple
                onChange={handleFile}
                disabled={readOnly}
                style={{display: "none"}}
                ref={fileRef}
            />

            {
                header &&
                <>
                    <span style={{ whiteSpace: 'nowrap', marginRight: '1rem' }} className="text-lg">Anexos</span>
                    <div style={{ flex: 1, borderBottom: '1px solid #ccc' }}></div>
                </>
            }

            <div className={isVersion ? "h-[150px]" : ""}>
                <AgGridReact
                    ref={gridRef}
                    rowHeight={32}
                    rowData={attaches}
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    animateRows={false}
                    suppressRowClickSelection={true}
                    singleClickEdit={true}
                    stopEditingWhenCellsLoseFocus={true}
                    gridOptions={{localeText: LocalizationAgGrid}}
                    onRowDataUpdated={notifyAttachesChange}
                    onCellValueChanged={notifyAttachesChange}
                    domLayout={isVersion ? 'normal' : 'autoHeight'}
                    context={{ addContent }}
                    className={"mt-4"}
                />
            </div>
            {
                canAdd && !onlySeeAttendances && !readOnly &&
                <>
                    <div className={"justify-left"}>
                        <Button onClick={addContent} target="primary" col={isVersion ? "mt-6" : "mt-2"}>
                            <FaPlus/>
                        </Button>
                    </div>
                </>
            }

            <Modal isOpen={open} alignTop={modalBody === "img"} onClose={() => {
                setOpen(false)
                setEditingRowIndex(null)
            }} title={"Anexo"}>
                {modalBody === "pdf/txt" && (
                    <div className={"flex justify-center mt-3"}>
                        <a
                            href={imgUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xl cursor-pointer text-blue-500 underline">
                            Clique aqui para visualizar
                        </a>
                    </div>

                )}
                {modalBody === "img" &&
                    (
                    <div style={{height: "600px", background: `url(${imgUrl}) center center / contain no-repeat`, backgroundSize: "contain"}} className={"m-5"} onClick={() => setOpen(false)}></div>
                )}

                <div className="flex flex-col lg:grid grid-cols-1 gap-x-6 gap-y-4 lg:grid-cols-12 m-5">
                    <InputForm id="description" name="description"  label="Descrição" col="col-span-12"
                               onChange={({ target: { value } }) => handleInput("description", value)}
                               value={modalData.description}
                               disabled={readOnly}
                    />
                </div>

                <div className="flex justify-end gap-2 px-3 py-3 border-t border-gray-300 bg-[#F2F2F2] rounded-b-lg">
                    <div className="flex justify-end gap-3">
                        <AttachButtonGroupCellRenderer node={modalNode} api={modalApi} setOpen={setOpen}
                                                       submit={submit} setEditingRowIndex={setEditingRowIndex}
                                                       readOnly={readOnly}/>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default AttachesTable;
